import React from 'react'
import ReactDOM from 'react-dom/client'
import i18n from '../../translation/i18n'
import { useTranslation } from 'react-i18next'
import { SearchBar } from '../../components/search-bar/SearchBar'
import { MainSearchBar } from '../../components/search-bar/MainSearchBar'
import { PlanContents } from '../../components/plan-contents/PlanContents'
import { PlanItem } from '../../components/plan-item/PlanItem'
import { SearchCalendar } from '../../components/landing-page/availability-tab/SearchCalendar'
import { LpAvailability } from '../../components/landing-page/availability-tab/LpAvailability'
import { RoomInfoButton } from '../../components/landing-page/room-info-button'
import { DetailFilterBar, FilterSectionKey } from '../../components/search-bar/DetailFilterBar'
import { DetailFilterModal } from '../../components/search-bar/DetailFilterModal'
import { SelectedTagsBar } from '../../components/search-bar/SelectedTagsBar'
import DetailFilterPanel from '../../components/search-bar/DetailFilterPanel'
import { PopularConditions } from '../../components/search-bar/PopularConditions'
import { getSections } from '../../components/search-bar/constants'
import { JpeTopPage } from '../../components/jpe-top-page/JpeTopPage'
import { HeaderCurrencyMenu } from '../../components/jpe-top-page/search-elements/HeaderCurrencyMenu'
import { ImageCarousel } from '../../components/jpe-top-page/nureseries/ImageCarousel'
import { JpeEmptyPlan } from '../../components/landing-page/jpe-components/JpeEmptyPlan'
import { PlanCalendar } from '../../components/plan-calendar'

const isJPE = i18n.language === 'en'
const SearchBarComponent = isJPE ? SearchBar : MainSearchBar

function getSelectedTagsFromUrl(sections) {
  const params = new URLSearchParams(window.location.search)
  const tags = []
  const processed = new Set()
  sections.forEach((section) => {
    section.tags.forEach((tag) => {
      const values = params.get(tag.key)?.split(',') || []
      const keyValue = `${tag.key}:${tag.value}`
      if (values.includes(tag.value) && !processed.has(keyValue)) {
        processed.add(keyValue)
        tags.push({ label: tag.label, value: tag.value, section: section.id, queryKey: tag.key })
      }
    })
  })
  return tags
}

function handleRemoveTag(tag) {
  const params = new URLSearchParams(window.location.search)
  const values = params.get(tag.queryKey)?.split(',') || []
  const newValues = values.filter((v) => v !== tag.value)
  if (newValues.length) {
    params.set(tag.queryKey, newValues.join(','))
  } else {
    params.delete(tag.queryKey)
  }
  if (params.get('page')) params.set('page', '1')
  window.location.href = params.toString() ? `?${params.toString()}` : '/'
}

function PopularConditionsPc() {
  return <PopularConditions layout="pc" />
}

function PopularConditionsMobile() {
  return <PopularConditions layout="mobile" />
}

function SelectedTagsBarWrapper() {
  const { t } = useTranslation()
  const sections = React.useMemo(() => getSections(t), [t])
  const [tags, setTags] = React.useState(() => getSelectedTagsFromUrl(sections))

  React.useEffect(() => {
    const onPopState = () => setTags(getSelectedTagsFromUrl(sections))
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [sections])

  return <SelectedTagsBar tags={tags} onRemove={handleRemoveTag} />
}

// Render React components by id
const reactElements = {
  'search-bar-mobile': SearchBarComponent,
  'search-bar-pc': SearchBarComponent,
  'plan-item': PlanItem,
  'search-calendar': SearchCalendar,
  'lp-availability': LpAvailability,
  'plan-calendar': PlanCalendar,
  'detail-filter-bar-pc': DetailFilterPanel,
  'detail-filter-bar-mobile': DetailFilterBarWithModalAndTags,
  'popular-conditions-pc': PopularConditionsPc,
  'popular-conditions-mobile': PopularConditionsMobile,
  'selected-tags-bar-pc': SelectedTagsBarWrapper,
  'selected-tags-bar-mobile': SelectedTagsBarWrapper,
  'jpe-top-page-root': JpeTopPage,
  'header-currency-menu': HeaderCurrencyMenu,
  'jpe-empty-plan': JpeEmptyPlan,
}

// モーダルの状態管理を外に出す
function DetailFilterBarWithModalAndTags() {
  const { t } = useTranslation()
  const sections = React.useMemo(() => getSections(t), [t])
  const [modalOpen, setModalOpen] = React.useState(false)
  const [scrollSection, setScrollSection] = React.useState<FilterSectionKey | undefined>(undefined)
  const [_selectedTags, setSelectedTags] = React.useState(() => getSelectedTagsFromUrl(sections))

  React.useEffect(() => {
    const onPopState = () => setSelectedTags(getSelectedTagsFromUrl(sections))
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [sections])

  React.useEffect(() => {
    const handleOpenDetailFilterModal = () => {
      setModalOpen(true)
    }

    window.addEventListener('open-detail-filter-modal', handleOpenDetailFilterModal)
    return () => window.removeEventListener('open-detail-filter-modal', handleOpenDetailFilterModal)
  }, [])

  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const shouldOpenFilterModal = queryParams.get('filter_modal') === 'true'
    const isMobile = window.innerWidth < 768
    if (shouldOpenFilterModal && isMobile) {
      setModalOpen(true)
    }
  }, [])

  const handleOpenModal = (sectionKey: FilterSectionKey) => {
    setScrollSection(sectionKey)
    setModalOpen(true)
  }

  return (
    <>
      <DetailFilterBar onOpenModal={handleOpenModal} />
      <DetailFilterModal open={modalOpen} onClose={() => setModalOpen(false)} scrollToSectionKey={scrollSection} />
    </>
  )
}

// Render standard components by ID
Object.entries(reactElements).forEach(([id, Component]) => {
  const element = document.getElementById(id) as HTMLElement
  if (element) {
    ReactDOM.createRoot(element).render(
      <React.StrictMode>
        <Component />
      </React.StrictMode>,
    )
  }
})

// Render React components by class
// Plan contents in top page of JPE
const planContents = document.getElementsByClassName('plan-contents') as HTMLCollectionOf<HTMLElement>
Array.from(planContents).forEach((element, index) => {
  const dataPlans = element.getAttribute('data-plans')
  const isShowOptions = !window.location.pathname.includes('plans') && index === 0

  ReactDOM.createRoot(element).render(
    <React.StrictMode>
      <PlanContents dataContent={JSON.parse(dataPlans)} isShowOptions={isShowOptions} />
    </React.StrictMode>,
  )
})

// Room info buttons with room data in landing page
const roomInfoButtons = document.getElementsByClassName('room-info-button') as HTMLCollectionOf<HTMLElement>
Array.from(roomInfoButtons).forEach((element) => {
  const roomData = element.getAttribute('data-room')
  ReactDOM.createRoot(element).render(
    <React.StrictMode>
      <RoomInfoButton room={JSON.parse(roomData)} />
    </React.StrictMode>,
  )
})

// Render card image carousel
const mountedCarousels = document.getElementsByClassName('nursery-image-carousel') as HTMLCollectionOf<HTMLElement>
Array.from(mountedCarousels).forEach((element) => {
  const imagesJson = element.getAttribute('data-images') || '[]'
  const carouselHeight = Number(element.getAttribute('data-carousel-height')) || 240
  const priority = element.hasAttribute('data-priority')

  // HAML が 1 枚目の画像を先に出している。createRoot の描画はそれを消して作り直す（同じ URL なので読み直さない）
  ReactDOM.createRoot(element).render(
    <React.StrictMode>
      <ImageCarousel images={JSON.parse(imagesJson)} carouselHeight={carouselHeight} priority={priority} />
    </React.StrictMode>,
  )
})
